import React from 'react';
import { Box, CircularProgress, Typography, alpha, useTheme as useMuiTheme } from '@mui/material';
import { useTheme } from '../context/ThemeContext'; 

interface LoadingSpinnerProps {
  message?: string;
  fullScreen?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  message = 'Loading...', 
  fullScreen = true,
  size = 'medium'
}) => {
  const muiTheme = useMuiTheme();
  const { mode } = useTheme();
  
  const isDark = mode === 'dark';

  // Size mapping - Made text larger and bolder
  const sizeMap = {
    small: { 
      spinner: 30,  // Increased from 24
      fontSize: '1rem',  // Increased from 0.875rem
      fontWeight: 700  // Added bold
    },
    medium: { 
      spinner: 50,  // Increased from 40
      fontSize: '1.25rem',  // Increased from 1rem
      fontWeight: 800  // Extra bold
    },
    large: { 
      spinner: 70,  // Increased from 60
      fontSize: '1.5rem',  // Increased from 1.25rem
      fontWeight: 900  // Heavy bold
    }
  };

  const currentSize = sizeMap[size];

  const spinnerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}> {/* Increased gap from 2 to 3 */}
      <CircularProgress 
        size={currentSize.spinner} 
        sx={{ 
          color: isDark ? '#C4B66F' : '#051814',
          mb: 1  // Added margin bottom
        }} 
      />
      {message && (
        <Typography 
          variant="h6"  // Changed from body1 to h6 for bolder default
          sx={{ 
            color: isDark ? '#FFFFFF' : '#051814',
            fontSize: currentSize.fontSize,
            fontWeight: currentSize.fontWeight,
            letterSpacing: '0.02em',  // Added letter spacing
            fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif'  // Added font family
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );

  if (!fullScreen) {
    return spinnerContent;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: isDark ? '#000000' : '#FAFAFA'
      }}
    >
      {spinnerContent}
    </Box>
  );
};

export default LoadingSpinner;